import { useState, type FormEvent } from 'react';
import type { NewReminderPayload, ReminderCadence } from '../hooks/useReminders';
import type { ReminderType } from '../types';

interface AddReminderFormProps {
  petId: string;
  onSubmit: (payload: NewReminderPayload) => Promise<void>;
  onClose: () => void;
}

const REMINDER_TYPE_OPTIONS: { value: ReminderType; label: string; emoji: string }[] = [
  { value: 'vaccine', label: 'Vaccine', emoji: '💉' },
  { value: 'medication', label: 'Meds', emoji: '💊' },
  { value: 'checkup', label: 'Checkup', emoji: '🩺' },
  { value: 'grooming', label: 'Grooming', emoji: '✂️' },
  { value: 'other', label: 'Other', emoji: '📌' },
];

const CADENCE_OPTIONS: { value: ReminderCadence; label: string; emoji: string }[] = [
  { value: 'once',    label: 'One-time',  emoji: '1️⃣' },
  { value: 'daily',   label: 'Daily',     emoji: '📅' },
  { value: 'weekly',  label: 'Weekly',    emoji: '🗓' },
  { value: 'monthly', label: 'Monthly',   emoji: '📆' },
];

/**
 * Combine `<input type="date">` (yyyy-mm-dd) and `<input type="time">` (HH:mm)
 * into an instant using the **browser's local timezone** (Asia/Kolkata → IST).
 *
 * Avoids `new Date("yyyy-mm-ddThh:mm:ss")`, which some engines treat as UTC.
 */
function localWallClockToDate(dateStr: string, timeStr: string): Date | null {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!dm) return null;
  const y = Number(dm[1]);
  const mo = Number(dm[2]);
  const d = Number(dm[3]);

  const tm = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(timeStr.trim());
  if (!tm) return null;
  const h = Number(tm[1]);
  const mi = Number(tm[2]);
  const s = tm[3] !== undefined ? Number(tm[3]) : 0;

  const dt = new Date(y, mo - 1, d, h, mi, s, 0);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
}

function AddReminderForm({ petId, onSubmit, onClose }: AddReminderFormProps) {
  const [title, setTitle] = useState('');
  const [reminderType, setReminderType] = useState<ReminderType>('vaccine');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('09:00');
  const [cadence, setCadence] = useState<ReminderCadence>('once');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg('');

    if (!title.trim()) { setErrorMsg('Reminder title is required.'); return; }
    if (!dueDate)       { setErrorMsg('Due date is required.'); return; }

    const dueDatetime = localWallClockToDate(dueDate, dueTime);
    if (!dueDatetime) { setErrorMsg('Invalid date or time.'); return; }

    setIsSubmitting(true);
    try {
      await onSubmit({
        pet_id:    petId,
        title:     title.trim(),
        reminder_type: reminderType,
        due_date:  dueDatetime.toISOString(),
        recurring: cadence,
      });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to save reminder.');
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reminder-form-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full sm:max-w-md bg-brand-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 id="reminder-form-title" className="font-display text-xl font-bold text-white">
            Add Reminder
          </h2>
          <button id="close-reminder-form" onClick={onClose} aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-xl text-white/50 hover:text-white hover:bg-white/10 transition-all">
            ✕
          </button>
        </div>

        <form id="add-reminder-form" onSubmit={(e) => void handleSubmit(e)} noValidate className="space-y-4">

          {/* Title */}
          <div>
            <label htmlFor="reminder-title" className="block text-sm font-medium text-white/80 mb-1">
              Reminder Title <span className="text-red-400">*</span>
            </label>
            <input
              id="reminder-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Rabies vaccine booster"
              className="input-field"
              required
            />
          </div>

          {/* Reminder type — required by DB (`reminder_type` NOT NULL) */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Type</label>
            <div className="grid grid-cols-5 gap-1.5">
              {REMINDER_TYPE_OPTIONS.map(({ value, label, emoji }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setReminderType(value)}
                  className={`flex flex-col items-center gap-0.5 py-2 px-0.5 rounded-xl border text-[10px] font-medium transition-all ${
                    reminderType === value
                      ? 'bg-brand-500 border-brand-400 text-white'
                      : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                  }`}
                >
                  <span className="text-base leading-none">{emoji}</span>
                  <span className="leading-tight text-center">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Date + Time row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="reminder-date" className="block text-sm font-medium text-white/80 mb-1">
                Date <span className="text-red-400">*</span>
              </label>
              <input
                id="reminder-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="input-field [color-scheme:dark]"
                required
              />
            </div>
            <div>
              <label htmlFor="reminder-time" className="block text-sm font-medium text-white/80 mb-1">
                Time
              </label>
              <input
                id="reminder-time"
                type="time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                className="input-field [color-scheme:dark]"
              />
            </div>
          </div>

          {/* Cadence picker */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Repeat</label>
            <div className="grid grid-cols-4 gap-2">
              {CADENCE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  id={`cadence-${opt.value}`}
                  onClick={() => setCadence(opt.value)}
                  className={`flex flex-col items-center gap-1 py-2 rounded-xl border text-xs font-medium transition-all duration-200 ${
                    cadence === opt.value
                      ? 'bg-brand-500 border-brand-400 text-white'
                      : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                  }`}
                >
                  <span className="text-base">{opt.emoji}</span>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {errorMsg && (
            <div role="alert" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
              {errorMsg}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button type="button" id="cancel-reminder" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              id="submit-reminder"
              disabled={isSubmitting}
              className="btn-primary flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                  Saving…
                </>
              ) : (
                '🔔 Set Reminder'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddReminderForm;
